/**
 * @quotech/ui - Mobile Drawer Component
 * 
 * Mobile sidebar drawer for responsive navigation.
 * 
 * Supports two open modes:
 * - 'hover': Drawer auto-closes when mouse leaves the drawer area (for pointer devices)
 * - 'click': Drawer stays open until backdrop is clicked/tapped (for touch devices)
 */

import { useEffect, useState, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '../lib/utils'

export interface MobileDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean
  /** Callback to close the drawer */
  onClose: () => void
  /** How the drawer was opened: 'hover' (auto-close on leave) or 'click' (stays open) */
  openMode?: 'hover' | 'click' | null
  /** Drawer content (typically Sidebar content) */
  children: React.ReactNode
  /** Custom className */
  className?: string
}

export function MobileDrawer({
  isOpen,
  onClose,
  openMode = 'click',
  children,
  className,
}: MobileDrawerProps) {
  // Track when backdrop should respond to clicks
  // This prevents the synthetic click from touch events from immediately closing the drawer
  const [backdropActive, setBackdropActive] = useState(false)
  const openTimeRef = useRef<number>(0)
  // Track if mouse is inside drawer (for hover mode)
  const isInsideDrawerRef = useRef(false)
  // Close timeout for hover mode (small delay to prevent flickering)
  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  
  // Prevent body scroll when open and manage backdrop activation
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      openTimeRef.current = Date.now()
      // Delay backdrop activation to prevent synthetic click from closing drawer
      const timer = setTimeout(() => setBackdropActive(true), 100)
      return () => clearTimeout(timer)
    } else {
      document.body.style.overflow = ''
      setBackdropActive(false)
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])
  
  // Clean up hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverCloseTimeoutRef.current) {
        clearTimeout(hoverCloseTimeoutRef.current)
      }
    }
  }, [])
  
  // Handle backdrop click with time-based guard
  const handleBackdropClick = () => {
    // Only close if drawer has been open for at least 150ms
    // This prevents touch + synthetic click race condition
    if (Date.now() - openTimeRef.current > 150) {
      onClose()
    }
  }
  
  // Handle mouse entering the drawer area (for hover mode)
  const handleDrawerMouseEnter = () => {
    isInsideDrawerRef.current = true
    // Cancel any pending close timeout
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current)
      hoverCloseTimeoutRef.current = null
    }
  }
  
  // Handle mouse leaving the drawer area (for hover mode)
  const handleDrawerMouseLeave = () => {
    isInsideDrawerRef.current = false
    // Only auto-close if opened via hover (not click/touch)
    if (openMode === 'hover' && isOpen) {
      // Small delay to prevent accidental close from minor mouse movements
      hoverCloseTimeoutRef.current = setTimeout(() => {
        if (!isInsideDrawerRef.current) {
          onClose()
        }
      }, 150)
    }
  }

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={handleBackdropClick}
          onTouchEnd={(e) => {
            // Prevent click from firing after touch
            if (Date.now() - openTimeRef.current > 150) {
              e.preventDefault()
              onClose()
            }
          }}
        />
      )}

      {/* Drawer */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 flex flex-col transition-transform duration-300 lg:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          className
        )}
        onMouseEnter={handleDrawerMouseEnter}
        onMouseLeave={handleDrawerMouseLeave}
      >
        {/* Close button - positioned to avoid overlap with sidebar header */}
        <button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onClose()
          }}
          onTouchEnd={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onClose()
          }}
          className="absolute top-3 right-3 p-1.5 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 z-20 shadow-sm"
          aria-label="Close menu"
          style={{
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <X className="w-4 h-4 text-slate-600 dark:text-slate-300" />
        </button>

        {/* Content - with padding-top to avoid close button overlap */}
        <div className="flex flex-col h-full pt-12">
          {children}
        </div>
      </aside>
    </>
  )
}

export default MobileDrawer

